import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['fields', 'security', 'recaptcha']
  static values = { recaptcha: Boolean }

  connect() {
    const alert = this.element.querySelector('[role="alert"]')
    const alertVisible = alert && !alert.closest('[hidden]')
    if (alertVisible) alert.focus()

    if (this.hasSecurityTarget && !this.securityTarget.hidden) {
      this.renderRecaptcha()
      if (!alertVisible) this.focusSecurityHeading()
    }
  }

  probe(event) {
    if (!this.recaptchaValue || this.allowNative) return
    if (window.AwsWafIntegration?.hasToken && !window.AwsWafIntegration.hasToken()) return

    event.preventDefault()
    this.askServer()
  }

  async askServer() {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    try {
      const response = await fetch(this.element.action, {
        method: 'POST',
        body: new FormData(this.element),
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': token,
          'X-Join-Check': '1',
        },
        redirect: 'manual',
      })
      const payload = await response.json().catch(() => ({}))
      if (payload.errors?.length && this.showProbeErrors(payload.errors)) return
      if (payload.step === 'security') {
        this.openSecurity()
        return
      }
      if (payload.redirect) {
        window.location.assign(payload.redirect)
        return
      }
    } catch {
      // A failed probe falls through to the normal submit.
    }

    this.enableJoinSubmit()
    this.allowNative = true
    this.element.requestSubmit()
  }

  openSecurity() {
    if (this.hasFieldsTarget) this.fieldsTarget.hidden = true
    if (!this.hasSecurityTarget) return

    this.securityTarget.hidden = false
    this.renderRecaptcha()
    this.focusSecurityHeading()
    this.releaseJoinSubmit()
  }

  showProbeErrors(errors) {
    const alert = this.element.querySelector('#join-errors')
    if (!alert) return false

    this.clearFieldErrors()
    alert.replaceChildren()
    errors.forEach((error, index) => {
      const paragraph = document.createElement('p')
      paragraph.id = `join-error-${error.attribute}-${index}`
      paragraph.textContent = error.message
      alert.append(paragraph)
      this.markField(error, index)
    })
    alert.hidden = false
    alert.focus()
    this.releaseJoinSubmit()
    return true
  }

  // jquery-ujs disables a data-disable-with submit 13ms after submit, expecting
  // the browser to leave the page. A probe stays here, so the button has to
  // come back after that timer.
  releaseJoinSubmit() {
    this.enableJoinSubmit()
    window.setTimeout(() => this.enableJoinSubmit(), 50)
  }

  enableJoinSubmit() {
    const button = this.element.querySelector('input[type="submit"], button[type="submit"]')
    if (!button) return

    const rails = window.jQuery?.rails
    if (rails) rails.enableFormElement(window.jQuery(button))
    else button.disabled = false
  }

  clearFieldErrors() {
    this.element.querySelectorAll('[aria-invalid]').forEach((input) => {
      input.removeAttribute('aria-invalid')
      const described = (input.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && !id.startsWith('join-error-'))
      if (described.length) input.setAttribute('aria-describedby', described.join(' '))
      else input.removeAttribute('aria-describedby')
    })
  }

  markField(error, index) {
    const name = error.attribute === 'base' ? 'email' : error.attribute
    const input = this.element.querySelector(`[name="user[${name}]"]`)
    if (!input) return

    input.setAttribute('aria-invalid', 'true')
    const described = new Set(
      (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean),
    )
    described.add(`join-error-${error.attribute}-${index}`)
    input.setAttribute('aria-describedby', [...described].join(' '))
  }

  focusSecurityHeading() {
    const heading = this.securityTarget.querySelector('h2')
    if (!heading) return

    heading.focus()
    window.getSelection()?.removeAllRanges()
  }

  renderRecaptcha() {
    if (!this.hasRecaptchaTarget) return

    const mount = this.recaptchaTarget
    if (mount.dataset.mounted) return

    const sitekey = mount.dataset.sitekey
    if (!sitekey) return
    if (window.grecaptcha?.render) {
      window.grecaptcha.ready(() => this.mountRecaptcha(mount, sitekey))
      return
    }

    this.pendingRecaptcha = { mount, sitekey }
    if (document.querySelector('script[data-join-recaptcha]')) return

    window.joinMountRecaptcha = () => {
      const pending = this.pendingRecaptcha
      if (pending) this.mountRecaptcha(pending.mount, pending.sitekey)
    }
    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?onload=joinMountRecaptcha&render=explicit'
    script.async = true
    script.dataset.joinRecaptcha = '1'
    document.head.appendChild(script)
  }

  mountRecaptcha(mount, sitekey) {
    if (!mount || mount.dataset.mounted || !window.grecaptcha?.render) return

    mount.dataset.mounted = '1'
    window.grecaptcha.render(mount, {
      sitekey,
      callback: (token) => this.finishSecurityCheck(token),
      'expired-callback': () => {
        this.allowNative = false
      },
    })
  }

  finishSecurityCheck(token) {
    this.storeRecaptchaToken(token)
    this.allowNative = true
    this.element.requestSubmit()
  }

  storeRecaptchaToken(token) {
    let field = this.element.querySelector('textarea[name="g-recaptcha-response"]')
    if (!field) {
      field = document.createElement('textarea')
      field.name = 'g-recaptcha-response'
      field.hidden = true
      this.element.appendChild(field)
    }
    field.value = token
  }

  togglePassword(event) {
    const button = event.currentTarget
    const input = button.parentElement.querySelector('input')
    const revealed = input.type === 'text'
    input.type = revealed ? 'password' : 'text'
    const verb = revealed ? 'Show' : 'Hide'
    button.textContent = verb
    button.setAttribute('aria-pressed', revealed ? 'false' : 'true')
    const field = button.dataset.passwordLabel || 'password'
    button.setAttribute('aria-label', `${verb} ${field}`)
  }
}
